import React, { useCallback, useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { components } from 'react-select';

/**
 * react-select menu footer + near-end scroll / IntersectionObserver loading
 * (shared by process and control association pickers).
 */
export const PaginatedSelectMenuList = (props: any) => {
  const lastNearEndAtRef = useRef(0);
  const menuNodeRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const { children, innerProps = {}, innerRef, ...rest } = props;
  const { onScroll: innerOnScroll, ...innerRest } = innerProps;
  const rawSp = rest.selectProps;
  const sp =
    rawSp && typeof rawSp === 'object'
      ? { ...rawSp, ...(rawSp.selectProps && typeof rawSp.selectProps === 'object' ? rawSp.selectProps : {}) }
      : {};
  const loadingMore = Boolean(sp.loadingMoreOptions);
  const hasMore = Boolean(sp.hasMoreOptions);
  const onNearEnd = sp.onOptionsNearEnd as (() => void) | undefined;

  const composedInnerRef = useCallback(
    (node: HTMLDivElement | null) => {
      menuNodeRef.current = node;
      if (typeof innerRef === 'function') {
        innerRef(node);
      } else if (innerRef && typeof innerRef === 'object' && 'current' in innerRef) {
        (innerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }
    },
    [innerRef]
  );

  const fireNearEnd = useCallback(() => {
    if (!hasMore || loadingMore || typeof onNearEnd !== 'function') return;
    const now = Date.now();
    if (now - lastNearEndAtRef.current < 350) return;
    lastNearEndAtRef.current = now;
    onNearEnd();
  }, [hasMore, loadingMore, onNearEnd]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    innerOnScroll?.(e);
    const el = e.currentTarget;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distance <= 48) fireNearEnd();
  };

  useEffect(() => {
    const root = menuNodeRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel || !hasMore) return undefined;

    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((en) => en.isIntersecting)) return;
        fireNearEnd();
      },
      { root, rootMargin: '0px 0px 80px 0px', threshold: 0 }
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [hasMore, loadingMore, fireNearEnd]);

  return (
    <components.MenuList {...rest} innerRef={composedInnerRef} innerProps={{ ...innerRest, onScroll: handleScroll }}>
      {children}
      <div ref={sentinelRef} className="h-1 w-full shrink-0" aria-hidden />
      {loadingMore ? (
        <div className="flex items-center justify-center border-t border-slate-100 px-3 py-2">
          <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
        </div>
      ) : null}
    </components.MenuList>
  );
};
